import {whiteListByPermission, type Env} from '@hconnect/common/hproduce/whiteListCheck'
import {
  Notifications,
  NotificationsOptions,
  TranslationActionKey,
  TranslationKey,
  Translations,
  generateNotificationChannels
} from '@hconnect/common/notifications'
import {useLoginContext, useUserContext} from '@hconnect/login'
import {getTimezoneName} from '@hconnect/uikit'
import {CommonHeader, NavItem, useGuidedTours} from '@hconnect/uikit/src/lib2'
import {getPlantName, useUpmPlantsContext} from '@hconnect/upm'
import {usePermissionsParsedQuery} from '@hproduce/permissions'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, useNavigate, useLocation} from 'react-router'

import {APP_NAME} from '../common/constants'
import {ACTIONS, COMPARATIVE_ANALYSIS, DASHBOARD, REF_LINKS} from '../common/routing'
import {usePlantUpmIdFromRoute} from '../hooks/usePlantIdFromRoute'
import {supportedLanguages} from '../localization'

const useNavItems = (plantId): NavItem[] => {
  const {t} = useTranslation()

  return useMemo(
    () =>
      plantId
        ? [
            {
              label: t('navigation.dashboard'),
              url: generatePath(DASHBOARD, {plantId}),
              dataTestId: 'nav-dashboard'
            },
            {
              label: t('navigation.actions'),
              url: generatePath(ACTIONS, {plantId}),
              dataTestId: 'nav-actions'
            },
            {
              label: t('navigation.comparativeAnalysis'),
              url: generatePath(COMPARATIVE_ANALYSIS, {plantId}),
              dataTestId: 'nav-comparative-analysis'
            },
            {
              label: t('navigation.referenceLinks'),
              url: generatePath(REF_LINKS, {plantId}),
              dataTestId: 'nav-reference-links'
            }
          ]
        : [],
    [plantId, t]
  )
}

const NOTIFICATIONS_TRANSLATION_KEYS: Translations = {
  label: {
    [TranslationKey.TODAY]: 'notifications.label.today',
    [TranslationKey.YESTERDAY]: 'notifications.label.yesterday',
    [TranslationKey.NOTIFICATIONS]: 'notifications.label.notifications',
    [TranslationKey.EMPTY]: 'notifications.label.empty',
    [TranslationKey.ERROR]: 'errors.notificationsNotFetched',
    [TranslationKey.SHOULD_DELETE]: 'notifications.label.shouldDeleteNotification'
  },
  action: {
    [TranslationActionKey.DELETE]: 'notifications.action.delete',
    [TranslationActionKey.CONFIRM_DELETE]: 'notifications.action.confirmDelete',
    [TranslationActionKey.CANCEL]: 'notifications.action.cancel'
  }
}

export const ShellHeader: React.FC = () => {
  const {loggedUser: user} = useUserContext()
  const {logout} = useLoginContext()
  const plantId = usePlantUpmIdFromRoute()
  const navItems = useNavItems(plantId)
  const navigate = useNavigate()
  const location = useLocation()
  const {tours, tourStatuses} = useGuidedTours()
  const timeZone = getTimezoneName()
  const {i18n} = useTranslation()

  const upmPlants = useUpmPlantsContext()

  const {data: permissions, error: permissionsError} = usePermissionsParsedQuery()

  const whiteList = whiteListByPermission(
    process.env.REACT_APP_STAGE as Env,
    permissions || [],
    upmPlants
  )

  const options = useMemo<NotificationsOptions | undefined>(() => {
    const channels = generateNotificationChannels(whiteList)
    return channels.length
      ? {
          channels,
          countryCode: i18n.language.toUpperCase().split('-')[0]
        }
      : undefined
  }, [whiteList, i18n.language])

  if (!user || !whiteList || permissionsError) {
    return <></>
  }

  return (
    <CommonHeader
      appName={APP_NAME}
      onLogout={logout}
      additionalActions={
        options
          ? [
              <Notifications
                key="notification-provider"
                options={options}
                translations={NOTIFICATIONS_TRANSLATION_KEYS}
                timezone={timeZone}
              />
            ]
          : undefined
      }
      plant={plantId ? {plantId, plantName: getPlantName(upmPlants, plantId)} : undefined}
      upmPlants={upmPlants}
      supportedLanguages={supportedLanguages}
      user={{...user, name: user.name ?? undefined}}
      whiteList={whiteList}
      env={process.env.REACT_APP_STAGE as Env}
      navItems={navItems}
      navigate={(url) => navigate(url)}
      locationPath={location.pathname}
      enableMyTasks
      guidedTours={{tours, statuses: tourStatuses}}
      closeDropdownOnLanguageSwitch
    />
  )
}
